import { useState, useRef, useEffect } from 'react';
import {
   Box,
   Button,
   Heading,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalFooter,
   ModalCloseButton,
   Select,
   Input,
   VStack,
   HStack,
   Alert,
   AlertIcon,
   useColorModeValue,
   FormControl,
   FormLabel,
   Divider,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   Checkbox,
   Text,
   Badge,
   TableContainer,
   Flex,
   Spacer,
} from '@chakra-ui/react';
import axios from '../../../../axios';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from 'contexts';
import { DAYBOOK_TYPE } from 'constants';
import { getDaybookForPayments } from '../../../../redux/actions/daybook';
import { getAccount } from '../../../../redux/actions/account';
import AddEditForm from 'views/admin/masters/Pages/CustomerMaster/AddEditForm';
import { getVoucher } from '../../../../redux/actions/voucher';

export default function BulkUpdateSuspenseAccount({
   isBulkUpdateSuspenseAccount,
   onClose,
   handleBulkUpdateSuspenseAccount,
}) {
   const dispatch = useDispatch();
   const [findName, setFindName] = useState('');
   const [accountName, setAccountName] = useState('');
   const accountsData = useSelector((state) => state.account.list || []);
   const [bankStatements, setBankStatements] = useState([]);
   const [selectedStatements, setSelectedStatements] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isImporting, setIsImporting] = useState(false);
   const { user } = useUser();
   const [message, setMessage] = useState('');
   const [selectedAccountId, setSelectedAccountId] = useState('');
   const [isOpen, setIsOpen] = useState(false);

   const userCompanyId = Number(user?.loginhistory?.companyId);
   const localCompanyId = Number(localStorage.getItem('companyId'));
   const validCompanyId =
      !isNaN(userCompanyId) && userCompanyId > 0
         ? userCompanyId
         : !isNaN(localCompanyId) && localCompanyId > 0
            ? localCompanyId
            : null;

   // Refactored Effect 1: Fetch initial data (Daybooks and Suspense Account)
   useEffect(() => {
      if (validCompanyId) {
         dispatch(getDaybookForPayments({ type: DAYBOOK_TYPE.BANK, companyId: validCompanyId }));
         dispatch(getAccount({ compId: validCompanyId, status: true }));
      } else {
         console.warn('Skipping initial data fetch: Invalid companyId');
      }
   }, [dispatch, validCompanyId]);

   const handleAccountSelection = (e) => {
      const selectedAccountId = e.target.value;
      const selectedAccount = accountsData.find(
         (account) => (account.id || account.value || account.accountId) === selectedAccountId,
      );

      setSelectedAccountId(selectedAccountId);
      setAccountName(selectedAccount ? selectedAccount.name : '');
   };

   const handleSave = () => {
      // Close the Add/Edit form modal
      setIsOpen(false);
      // Re-fetch the suspense account to get the newly created one
      if (validCompanyId) {
         // dispatch(getVoucher({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
      }
      // Give feedback to the user
      setMessage('Suspense Account created. Please try importing again.');
   };

   const extractBankStatement = async () => {
      if (!selectedAccountId) {
         setMessage('Please select Account Name');
         return;
      }

      setIsLoading(true);
      setMessage('');

      try {
         const params = {
            accountName: 'SUSPENCE',
            userNote: findName,
            isImported: 1,
            companyId: validCompanyId,
            page: 1,
            limit: 10,
            sortBy: 'id',
            sortOrder: 'desc',
         };

         const response = await axios.get('/voucher/all', {
            params: params,
         });

         const data = response.data.data;
         // Filter out records with 0 amount
         const filteredData = data
            ? data.filter((statement) => {
               const amount = Number(statement.amount);
               return amount !== 0 && !isNaN(amount);
            })
            : [];

         setBankStatements(filteredData);
         // setBankStatements(data || []);
         setSelectedStatements({}); // Reset selections when new data is loaded
      } catch (error) {
         setMessage(`Error: ${error.response.data.message}`);
      } finally {
         setIsLoading(false);
      }
   };

   // Handle individual checkbox selection
   const handleStatementSelection = (statementId, isChecked) => {
      setSelectedStatements((prev) => ({
         ...prev,
         [statementId]: isChecked,
      }));
   };

   // Handle select all checkbox
   const handleSelectAll = (isChecked) => {
      const newSelectedStatements = {};
      if (isChecked) {
         bankStatements.forEach((statement) => {
            newSelectedStatements[statement.id] = true;
         });
      }
      setSelectedStatements(newSelectedStatements);
   };

   // Check if all statements are selected
   const isAllSelected =
      bankStatements.length > 0 && bankStatements.every((statement) => selectedStatements[statement.id]);

   // Get selected statement IDs (excluding records with 0 amount)
   const getSelectedStatementIds = () => {
      return Object.keys(selectedStatements)
         .filter((id) => selectedStatements[id])
         .map((id) => parseInt(id))
         .filter((id) => {
            // Find the statement and ensure it doesn't have 0 amount
            const statement = bankStatements.find((s) => s.id === id);
            return statement && Number(statement.amount) !== 0;
         });
   };

   // Handle find and replace API call
   const handleFindAndReplace = async () => {
      const selectedIds = getSelectedStatementIds();

      if (selectedIds.length === 0) {
         setMessage('Please select at least one statement to update');
         return;
      }

      if (!selectedAccountId) {
         setMessage('Please select a target account');
         return;
      }

      // Enhanced validation for selectedAccountId
      if (!selectedAccountId) {
         setMessage('Selected account ID is missing. Please select a valid account.');
         console.error('selectedAccountId is empty:', selectedAccountId);
         console.error('accountName:', accountName);
         console.error('accountsData:', accountsData);
         return;
      }

      setIsImporting(true);
      setMessage('');

      try {
         // Enhanced payload with additional debugging info
         const payload = {
            statementIds: selectedIds,
            newAccountId: parseInt(selectedAccountId),
            companyId: validCompanyId,
         };

         const response = await axios.post('/voucher/bulk-suspense-update-account', payload);

         setMessage('Successfully updated selected statements');
         // Refresh the data after successful update
         extractBankStatement();
      } catch (error) {
         setMessage(`Error: ${error.response?.data?.message || error.message}`);
      } finally {
         setIsImporting(false);
      }
   };

   // Format amount based on transaction type
   const formatAmount = (amount, txnType) => {
      const formattedAmount = Number(amount).toLocaleString('en-IN', {
         style: 'currency',
         currency: 'INR',
         minimumFractionDigits: 2,
      });

      return txnType === 1 ? `${formattedAmount} (Credit)` : `${formattedAmount} (Debit)`;
   };

   const bgColor = useColorModeValue('gray.100', 'gray.700');
   const headerBgColor = useColorModeValue('blue.50', 'blue.900');

   return (
      <Modal isOpen={isBulkUpdateSuspenseAccount} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
         <ModalOverlay />
         <ModalContent bg={bgColor}>
            <ModalHeader display="flex" alignItems="center" bg={headerBgColor}>
               <Heading size="sm">Bulk Update Suspense Account</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <VStack spacing={6} align="stretch">
                  {/* Bank Selection and File Upload Section */}
                  <Box>
                     <HStack spacing={4} mb={4}>
                        <FormControl flex="1">
                           <FormLabel>Enter Account Name</FormLabel>
                           <Input
                              placeholder="Enter Account name"
                              value={findName}
                              onChange={(e) => setFindName(e.target.value)}
                           />
                        </FormControl>
                        <FormControl flex="1">
                           <FormLabel>Select Account</FormLabel>
                           <Select
                              placeholder="Choose a Account"
                              value={selectedAccountId}
                              onChange={handleAccountSelection}>
                              {accountsData.map((bank, index) => (
                                 <option
                                    key={`${bank.id || bank.value}-${index}`}
                                    value={bank.id || bank.value || bank.accountId}>
                                    {bank.name}
                                 </option>
                              ))}
                           </Select>
                        </FormControl>
                        <Button
                           colorScheme="blue"
                           onClick={extractBankStatement}
                           isLoading={isLoading}
                           loadingText="Finding..."
                           isDisabled={!selectedAccountId}
                           alignSelf="end">
                           Find
                        </Button>
                     </HStack>

                     {/* Debug information - remove in production */}
                     {selectedAccountId && (
                        <Box p={2} bg="gray.50" borderRadius="md" fontSize="sm" color="gray.600">
                           <Text>Debug Info:</Text>
                           <Text>Selected Account Name: {accountName}</Text>
                           <Text>Selected Account ID: {selectedAccountId}</Text>
                        </Box>
                     )}

                     {message && (
                        <Alert status={message.includes('Error') ? 'error' : 'success'} size="sm">
                           <AlertIcon />
                           {message}
                        </Alert>
                     )}
                  </Box>

                  <Divider />

                  {/* Bank Statements Table */}
                  {bankStatements.length > 0 && (
                     <Box>
                        <Flex mb={4}>
                           <Text fontSize="lg" fontWeight="semibold">
                              Bank Statements ({bankStatements.length} records)
                           </Text>
                           <Spacer />
                           <Text fontSize="sm" color="gray.500">
                              Selected: {getSelectedStatementIds().length} of {bankStatements.length}
                           </Text>
                        </Flex>

                        <TableContainer>
                           <Table variant="simple" size="sm">
                              <Thead>
                                 <Tr>
                                    <Th>
                                       <Checkbox
                                          isChecked={isAllSelected}
                                          onChange={(e) => handleSelectAll(e.target.checked)}
                                       />
                                    </Th>
                                    <Th>Account Name</Th>
                                    <Th>User Note</Th>
                                    <Th>Amount</Th>
                                    <Th>Transaction Date</Th>
                                 </Tr>
                              </Thead>
                              <Tbody>
                                 {bankStatements.map((statement) => (
                                    <Tr key={statement.id}>
                                       <Td>
                                          <Checkbox
                                             isChecked={selectedStatements[statement.id] || false}
                                             onChange={(e) => handleStatementSelection(statement.id, e.target.checked)}
                                          />
                                       </Td>
                                       <Td>
                                          <Badge colorScheme="blue" variant="subtle">
                                             {statement.accountName}
                                          </Badge>
                                       </Td>
                                       <Td>
                                          <Text fontSize="sm" maxWidth="300px" isTruncated>
                                             {statement.userNote || 'N/A'}
                                          </Text>
                                       </Td>
                                       <Td>
                                          <Text
                                             fontSize="sm"
                                             fontWeight="medium"
                                             color={statement.txnType === 1 ? 'green.600' : 'red.600'}>
                                             {formatAmount(statement.amount, statement.txnType)}
                                          </Text>
                                       </Td>
                                       <Td>
                                          <Text fontSize="sm">
                                             {new Date(statement.txnDate).toLocaleDateString('en-IN')}
                                          </Text>
                                       </Td>
                                    </Tr>
                                 ))}
                              </Tbody>
                           </Table>
                        </TableContainer>
                     </Box>
                  )}
               </VStack>
            </ModalBody>
            <ModalFooter justifyContent="flex-end">
               <Button
                  mr={2}
                  colorScheme="green"
                  size="sm"
                  onClick={handleFindAndReplace}
                  isLoading={isImporting}
                  loadingText="Updating..."
                  isDisabled={getSelectedStatementIds().length === 0 || !selectedAccountId}>
                  Find and Replace ({getSelectedStatementIds().length})
               </Button>
               <Button size="sm" onClick={onClose}>
                  Close
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
}

// import { useState, useRef, useEffect } from 'react';
// import {
//    Box,
//    Button,
//    Heading,
//    Modal,
//    ModalOverlay,
//    ModalContent,
//    ModalHeader,
//    ModalBody,
//    ModalFooter,
//    ModalCloseButton,
//    Select,
//    Input,
//    VStack,
//    HStack,
//    Alert,
//    AlertIcon,
//    useColorModeValue,
//    FormControl,
//    FormLabel,
//    Divider,
//    Table,
//    Thead,
//    Tbody,
//    Tr,
//    Th,
//    Td,
//    Checkbox,
//    Text,
//    Badge,
//    TableContainer,
//    Flex,
//    Spacer,
// } from '@chakra-ui/react';
// import axios from '../../../../axios';
// import { useDispatch, useSelector } from 'react-redux';
// import { useUser } from 'contexts';
// import { DAYBOOK_TYPE } from 'constants';
// import { getDaybookForPayments } from '../../../../redux/actions/daybook';
// import { getAccount } from '../../../../redux/actions/account';
// import AddEditForm from 'views/admin/masters/Pages/CustomerMaster/AddEditForm';
// import { getVoucher } from '../../../../redux/actions/voucher';

// export default function BulkUpdateSuspenseAccount({
//    isBulkUpdateSuspenseAccount,
//    onClose,
//    handleBulkUpdateSuspenseAccount,
// }) {
//    const dispatch = useDispatch();
//    const [findName, setFindName] = useState('');
//    const [accountName, setAccountName] = useState('');
//    const accountsData = useSelector((state) => state.account.list || []);
//    const [bankStatements, setBankStatements] = useState([]);
//    const [selectedStatements, setSelectedStatements] = useState({});
//    const [isLoading, setIsLoading] = useState(false);
//    const [isImporting, setIsImporting] = useState(false);
//    const { user } = useUser();
//    const [message, setMessage] = useState('');
//    const [selectedAccountId, setSelectedAccountId] = useState('');
//    const [isOpen, setIsOpen] = useState(false);

//    const userCompanyId = Number(user?.loginhistory?.companyId);
//    const localCompanyId = Number(localStorage.getItem('companyId'));
//    const validCompanyId =
//       !isNaN(userCompanyId) && userCompanyId > 0
//          ? userCompanyId
//          : !isNaN(localCompanyId) && localCompanyId > 0
//          ? localCompanyId
//          : null;

//    // Refactored Effect 1: Fetch initial data (Daybooks and Suspense Account)
//    useEffect(() => {
//       if (validCompanyId) {
//          dispatch(getDaybookForPayments({ type: DAYBOOK_TYPE.BANK, companyId: validCompanyId }));
//          dispatch(getAccount({ compId: validCompanyId }));
//       } else {
//          console.warn('Skipping initial data fetch: Invalid companyId');
//       }
//    }, [dispatch, validCompanyId]);

//    const handleAccountSelection = (e) => {
//       const selectedValue = e.target.value;
//       const selectedAccount = accountsData.find((account) => account.value === selectedValue);

//       console.log('Selected value:', selectedValue);
//       console.log('Selected account:', selectedAccount);
//       console.log('Accounts data:', accountsData);

//       setAccountName(selectedValue);

//       // Enhanced logic to get the account ID
//       if (selectedAccount) {
//          const accountId = selectedAccount.id || selectedAccount.value || selectedAccount.accountId;
//          setSelectedAccountId(accountId);
//          console.log('Setting selectedAccountId to:', accountId);
//       } else {
//          setSelectedAccountId('');
//          console.log('No account found, clearing selectedAccountId');
//       }
//    };

//    const handleSave = () => {
//       // Close the Add/Edit form modal
//       setIsOpen(false);
//       // Re-fetch the suspense account to get the newly created one
//       if (validCompanyId) {
//          // dispatch(getVoucher({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
//       }
//       // Give feedback to the user
//       setMessage('Suspense Account created. Please try importing again.');
//    };

//    const extractBankStatement = async () => {
//       if (!accountName) {
//          setMessage('Please select Account Name');
//          return;
//       }

//       setIsLoading(true);
//       setMessage('');

//       try {
//          const params = {
//             accountName: 'SUSPENCE',
//             userNote: findName,
//             isImported: 1,
//             companyId: validCompanyId,
//             page: 1,
//             limit: 10,
//             sortBy: 'id',
//             sortOrder: 'desc',
//          };

//          const response = await axios.get('/voucher/all', {
//             params: params,
//          });

//          const data = response.data.data;
//          console.log('data.', data);
//          setBankStatements(data || []);
//          setSelectedStatements({}); // Reset selections when new data is loaded
//       } catch (error) {
//          setMessage(`Error: ${error.response.data.message}`);
//       } finally {
//          setIsLoading(false);
//       }
//    };

//    // Handle individual checkbox selection
//    const handleStatementSelection = (statementId, isChecked) => {
//       setSelectedStatements((prev) => ({
//          ...prev,
//          [statementId]: isChecked,
//       }));
//    };

//    // Handle select all checkbox
//    const handleSelectAll = (isChecked) => {
//       const newSelectedStatements = {};
//       if (isChecked) {
//          bankStatements.forEach((statement) => {
//             newSelectedStatements[statement.id] = true;
//          });
//       }
//       setSelectedStatements(newSelectedStatements);
//    };

//    // Check if all statements are selected
//    const isAllSelected =
//       bankStatements.length > 0 && bankStatements.every((statement) => selectedStatements[statement.id]);

//    // Get selected statement IDs
//    const getSelectedStatementIds = () => {
//       return Object.keys(selectedStatements)
//          .filter((id) => selectedStatements[id])
//          .map((id) => parseInt(id));
//    };

//    // Handle find and replace API call
//    const handleFindAndReplace = async () => {
//       const selectedIds = getSelectedStatementIds();

//       if (selectedIds.length === 0) {
//          setMessage('Please select at least one statement to update');
//          return;
//       }

//       if (!accountName) {
//          setMessage('Please select a target account');
//          return;
//       }

//       // Enhanced validation for selectedAccountId
//       if (!selectedAccountId) {
//          setMessage('Selected account ID is missing. Please select a valid account.');
//          console.error('selectedAccountId is empty:', selectedAccountId);
//          console.error('accountName:', accountName);
//          console.error('accountsData:', accountsData);
//          return;
//       }

//       setIsImporting(true);
//       setMessage('');

//       try {
//          // Enhanced payload with additional debugging info
//          const payload = {
//             statementIds: selectedIds,
//             newAccountId: selectedAccountId,
//             companyId: validCompanyId,
//          };

//          console.log('Payload being sent:', payload);

//          const response = await axios.post('/voucher/bulk-update-account', payload);

//          setMessage('Successfully updated selected statements');
//          // Refresh the data after successful update
//          extractBankStatement();
//       } catch (error) {
//          setMessage(`Error: ${error.response?.data?.message || error.message}`);
//       } finally {
//          setIsImporting(false);
//       }
//    };

//    // Format amount based on transaction type
//    const formatAmount = (amount, txnType) => {
//       const formattedAmount = Number(amount).toLocaleString('en-IN', {
//          style: 'currency',
//          currency: 'INR',
//          minimumFractionDigits: 2,
//       });

//       return txnType === 1 ? `${formattedAmount} (Credit)` : `${formattedAmount} (Debit)`;
//    };

//    console.log('bankStatement', bankStatements);
//    console.log('selectedAccountId:', selectedAccountId); // Debug log
//    console.log('accountName:', accountName); // Debug log

//    const bgColor = useColorModeValue('gray.100', 'gray.700');
//    const headerBgColor = useColorModeValue('blue.50', 'blue.900');

//    return (
//       <Modal isOpen={isBulkUpdateSuspenseAccount} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
//          <ModalOverlay />
//          <ModalContent bg={bgColor}>
//             <ModalHeader display="flex" alignItems="center" bg={headerBgColor}>
//                <Heading size="sm">Bulk Update Suspense Account</Heading>
//             </ModalHeader>
//             <ModalCloseButton />
//             <ModalBody>
//                <VStack spacing={6} align="stretch">
//                   {/* Bank Selection and File Upload Section */}
//                   <Box>
//                      <HStack spacing={4} mb={4}>
//                         <FormControl flex="1">
//                            <FormLabel>Enter Account Name</FormLabel>
//                            <Input
//                               placeholder="Enter Account name"
//                               value={findName}
//                               onChange={(e) => setFindName(e.target.value)}
//                            />
//                         </FormControl>
//                         <FormControl flex="1">
//                            <FormLabel>Select Account</FormLabel>
//                            <Select placeholder="Choose a Account" value={accountName} onChange={handleAccountSelection}>
//                               {accountsData.map((bank, index) => (
//                                  <option key={`${bank.value}-${index}`} value={bank.value}>
//                                     {bank.name}
//                                  </option>
//                               ))}
//                            </Select>
//                         </FormControl>
//                         <Button
//                            colorScheme="blue"
//                            onClick={extractBankStatement}
//                            isLoading={isLoading}
//                            loadingText="Finding..."
//                            isDisabled={!accountName}
//                            alignSelf="end">
//                            Find
//                         </Button>
//                      </HStack>

//                      {/* Debug information - remove in production */}
//                      {accountName && (
//                         <Box p={2} bg="gray.50" borderRadius="md" fontSize="sm" color="gray.600">
//                            <Text>Debug Info:</Text>
//                            <Text>Selected Account Name: {accountName}</Text>
//                            <Text>Selected Account ID: {selectedAccountId || 'Not set'}</Text>
//                         </Box>
//                      )}

//                      {message && (
//                         <Alert status={message.includes('Error') ? 'error' : 'success'} size="sm">
//                            <AlertIcon />
//                            {message}
//                         </Alert>
//                      )}
//                   </Box>

//                   <Divider />

//                   {/* Bank Statements Table */}
//                   {bankStatements.length > 0 && (
//                      <Box>
//                         <Flex mb={4}>
//                            <Text fontSize="lg" fontWeight="semibold">
//                               Bank Statements ({bankStatements.length} records)
//                            </Text>
//                            <Spacer />
//                            <Text fontSize="sm" color="gray.500">
//                               Selected: {getSelectedStatementIds().length} of {bankStatements.length}
//                            </Text>
//                         </Flex>

//                         <TableContainer>
//                            <Table variant="simple" size="sm">
//                               <Thead>
//                                  <Tr>
//                                     <Th>
//                                        <Checkbox
//                                           isChecked={isAllSelected}
//                                           onChange={(e) => handleSelectAll(e.target.checked)}
//                                        />
//                                     </Th>
//                                     <Th>Account Name</Th>
//                                     <Th>User Note</Th>
//                                     <Th>Amount</Th>
//                                     <Th>Transaction Date</Th>
//                                  </Tr>
//                               </Thead>
//                               <Tbody>
//                                  {bankStatements.map((statement) => (
//                                     <Tr key={statement.id}>
//                                        <Td>
//                                           <Checkbox
//                                              isChecked={selectedStatements[statement.id] || false}
//                                              onChange={(e) => handleStatementSelection(statement.id, e.target.checked)}
//                                           />
//                                        </Td>
//                                        <Td>
//                                           <Badge colorScheme="blue" variant="subtle">
//                                              {statement.accountName}
//                                           </Badge>
//                                        </Td>
//                                        <Td>
//                                           <Text fontSize="sm" maxWidth="300px" isTruncated>
//                                              {statement.userNote || 'N/A'}
//                                           </Text>
//                                        </Td>
//                                        <Td>
//                                           <Text
//                                              fontSize="sm"
//                                              fontWeight="medium"
//                                              color={statement.txnType === 1 ? 'green.600' : 'red.600'}>
//                                              {formatAmount(statement.amount, statement.txnType)}
//                                           </Text>
//                                        </Td>
//                                        <Td>
//                                           <Text fontSize="sm">
//                                              {new Date(statement.txnDate).toLocaleDateString('en-IN')}
//                                           </Text>
//                                        </Td>
//                                     </Tr>
//                                  ))}
//                               </Tbody>
//                            </Table>
//                         </TableContainer>
//                      </Box>
//                   )}
//                </VStack>
//             </ModalBody>
//             <ModalFooter justifyContent="flex-end">
//                <Button
//                   mr={2}
//                   colorScheme="green"
//                   size="sm"
//                   onClick={handleFindAndReplace}
//                   isLoading={isImporting}
//                   loadingText="Updating..."
//                   isDisabled={getSelectedStatementIds().length === 0 || !accountName || !selectedAccountId}>
//                   Find and Replace ({getSelectedStatementIds().length})
//                </Button>
//                <Button size="sm" onClick={onClose}>
//                   Close
//                </Button>
//             </ModalFooter>
//          </ModalContent>
//       </Modal>
//    );
// }

// import { useState, useRef, useEffect } from 'react';
// import {
//    Box,
//    Button,
//    Heading,
//    Modal,
//    ModalOverlay,
//    ModalContent,
//    ModalHeader,
//    ModalBody,
//    ModalFooter,
//    ModalCloseButton,
//    Select,
//    Input,
//    VStack,
//    HStack,
//    Alert,
//    AlertIcon,
//    useColorModeValue,
//    FormControl,
//    FormLabel,
//    Divider,
//    Table,
//    Thead,
//    Tbody,
//    Tr,
//    Th,
//    Td,
//    Checkbox,
//    Text,
//    Badge,
//    TableContainer,
//    Flex,
//    Spacer,
// } from '@chakra-ui/react';
// import axios from '../../../../axios';
// import { useDispatch, useSelector } from 'react-redux';
// import { useUser } from 'contexts';
// import { DAYBOOK_TYPE } from 'constants';
// import { getDaybookForPayments } from '../../../../redux/actions/daybook';
// import { getAccount } from '../../../../redux/actions/account';
// import AddEditForm from 'views/admin/masters/Pages/CustomerMaster/AddEditForm';
// import { getVoucher } from '../../../../redux/actions/voucher';

// export default function BulkUpdateSuspenseAccount({
//    isBulkUpdateSuspenseAccount,
//    onClose,
//    handleBulkUpdateSuspenseAccount,
// }) {
//    const dispatch = useDispatch();
//    const [findName, setFindName] = useState('');
//    const [accountName, setAccountName] = useState('');
//    const accountsData = useSelector((state) => state.account.list || []);
//    const [bankStatements, setBankStatements] = useState([]);
//    const [selectedStatements, setSelectedStatements] = useState({});
//    const [isLoading, setIsLoading] = useState(false);
//    const [isImporting, setIsImporting] = useState(false);
//    const { user } = useUser();
//    const [message, setMessage] = useState('');
//    const [selectedAccountId, setSelectedAccountId] = useState('');
//    const [isOpen, setIsOpen] = useState(false);

//    const userCompanyId = Number(user?.loginhistory?.companyId);
//    const localCompanyId = Number(localStorage.getItem('companyId'));
//    const validCompanyId =
//       !isNaN(userCompanyId) && userCompanyId > 0
//          ? userCompanyId
//          : !isNaN(localCompanyId) && localCompanyId > 0
//          ? localCompanyId
//          : null;

//    // Refactored Effect 1: Fetch initial data (Daybooks and Suspense Account)
//    useEffect(() => {
//       if (validCompanyId) {
//          dispatch(getDaybookForPayments({ type: DAYBOOK_TYPE.BANK, companyId: validCompanyId }));
//          dispatch(getAccount({ compId: validCompanyId }));
//       } else {
//          console.warn('Skipping initial data fetch: Invalid companyId');
//       }
//    }, [dispatch, validCompanyId]);

//    const handleAccountSelection = (e) => {
//       const selectedValue = e.target.value;
//       const selectedAccount = accountsData.find((account) => account.value === selectedValue);

//       setAccountName(selectedValue);
//       setSelectedAccountId(selectedAccount ? selectedAccount.id || selectedAccount.value : '');
//    };

//    const handleSave = () => {
//       // Close the Add/Edit form modal
//       setIsOpen(false);
//       // Re-fetch the suspense account to get the newly created one
//       if (validCompanyId) {
//          // dispatch(getVoucher({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
//       }
//       // Give feedback to the user
//       setMessage('Suspense Account created. Please try importing again.');
//    };

//    const extractBankStatement = async () => {
//       if (!accountName) {
//          setMessage('Please select Account Name');
//          return;
//       }

//       setIsLoading(true);
//       setMessage('');

//       try {
//          const params = {
//             accountName: 'SUSPENCE',
//             userNote: findName,
//             isImported: 1,
//             companyId: validCompanyId,
//             page: 1,
//             limit: 10,
//             sortBy: 'id',
//             sortOrder: 'desc',
//          };

//          const response = await axios.get('/voucher/all', {
//             params: params,
//          });

//          const data = response.data.data;
//          console.log('data.', data);
//          setBankStatements(data || []);
//          setSelectedStatements({}); // Reset selections when new data is loaded
//       } catch (error) {
//          setMessage(`Error: ${error.response.data.message}`);
//       } finally {
//          setIsLoading(false);
//       }
//    };

//    // Handle individual checkbox selection
//    const handleStatementSelection = (statementId, isChecked) => {
//       setSelectedStatements((prev) => ({
//          ...prev,
//          [statementId]: isChecked,
//       }));
//    };

//    // Handle select all checkbox
//    const handleSelectAll = (isChecked) => {
//       const newSelectedStatements = {};
//       if (isChecked) {
//          bankStatements.forEach((statement) => {
//             newSelectedStatements[statement.id] = true;
//          });
//       }
//       setSelectedStatements(newSelectedStatements);
//    };

//    // Check if all statements are selected
//    const isAllSelected =
//       bankStatements.length > 0 && bankStatements.every((statement) => selectedStatements[statement.id]);

//    // Get selected statement IDs
//    const getSelectedStatementIds = () => {
//       return Object.keys(selectedStatements)
//          .filter((id) => selectedStatements[id])
//          .map((id) => parseInt(id));
//    };

//    // Handle find and replace API call
//    const handleFindAndReplace = async () => {
//       const selectedIds = getSelectedStatementIds();

//       if (selectedIds.length === 0) {
//          setMessage('Please select at least one statement to update');
//          return;
//       }

//       if (!accountName) {
//          setMessage('Please select a target account');
//          return;
//       }

//       setIsImporting(true);
//       setMessage('');

//       try {
//          // Replace API call - adjust the endpoint and payload according to your API
//          const payload = {
//             statementIds: selectedIds,
//             newAccountId: selectedAccountId,
//             companyId: validCompanyId,
//          };

//          const response = await axios.post('/voucher/bulk-update-account', payload);

//          setMessage('Successfully updated selected statements');
//          // Refresh the data after successful update
//          extractBankStatement();
//       } catch (error) {
//          setMessage(`Error: ${error.response?.data?.message || error.message}`);
//       } finally {
//          setIsImporting(false);
//       }
//    };

//    // Format amount based on transaction type
//    const formatAmount = (amount, txnType) => {
//       const formattedAmount = Number(amount).toLocaleString('en-IN', {
//          style: 'currency',
//          currency: 'INR',
//          minimumFractionDigits: 2,
//       });

//       return txnType === 1 ? `${formattedAmount} (Credit)` : `${formattedAmount} (Debit)`;
//    };

//    console.log('bankStatement', bankStatements);
//    const bgColor = useColorModeValue('gray.100', 'gray.700');
//    const headerBgColor = useColorModeValue('blue.50', 'blue.900');

//    return (
//       <Modal isOpen={isBulkUpdateSuspenseAccount} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
//          <ModalOverlay />
//          <ModalContent bg={bgColor}>
//             <ModalHeader display="flex" alignItems="center" bg={headerBgColor}>
//                <Heading size="sm">Bulk Update Suspense Account</Heading>
//             </ModalHeader>
//             <ModalCloseButton />
//             <ModalBody>
//                <VStack spacing={6} align="stretch">
//                   {/* Bank Selection and File Upload Section */}
//                   <Box>
//                      <HStack spacing={4} mb={4}>
//                         <FormControl flex="1">
//                            <FormLabel>Enter Account Name</FormLabel>
//                            <Input
//                               placeholder="Enter Account name"
//                               value={findName}
//                               onChange={(e) => setFindName(e.target.value)}
//                            />
//                         </FormControl>
//                         <FormControl flex="1">
//                            <FormLabel>Select Account</FormLabel>
//                            <Select placeholder="Choose a Account" value={accountName} onChange={handleAccountSelection}>
//                               {accountsData.map((bank, index) => (
//                                  <option key={`${bank.value}-${index}`} value={bank.value}>
//                                     {bank.name}
//                                  </option>
//                               ))}
//                            </Select>
//                         </FormControl>
//                         <Button
//                            colorScheme="blue"
//                            onClick={extractBankStatement}
//                            isLoading={isLoading}
//                            loadingText="Finding..."
//                            isDisabled={!accountName}
//                            alignSelf="end">
//                            Find
//                         </Button>
//                      </HStack>
//                      {message && (
//                         <Alert status={message.includes('Error') ? 'error' : 'success'} size="sm">
//                            <AlertIcon />
//                            {message}
//                         </Alert>
//                      )}
//                   </Box>

//                   <Divider />

//                   {/* Bank Statements Table */}
//                   {bankStatements.length > 0 && (
//                      <Box>
//                         <Flex mb={4}>
//                            <Text fontSize="lg" fontWeight="semibold">
//                               Bank Statements ({bankStatements.length} records)
//                            </Text>
//                            <Spacer />
//                            <Text fontSize="sm" color="gray.500">
//                               Selected: {getSelectedStatementIds().length} of {bankStatements.length}
//                            </Text>
//                         </Flex>

//                         <TableContainer>
//                            <Table variant="simple" size="sm">
//                               <Thead>
//                                  <Tr>
//                                     <Th>
//                                        <Checkbox
//                                           isChecked={isAllSelected}
//                                           onChange={(e) => handleSelectAll(e.target.checked)}
//                                        />
//                                     </Th>
//                                     <Th>Account Name</Th>
//                                     <Th>User Note</Th>
//                                     <Th>Amount</Th>
//                                     <Th>Transaction Date</Th>
//                                  </Tr>
//                               </Thead>
//                               <Tbody>
//                                  {bankStatements.map((statement) => (
//                                     <Tr key={statement.id}>
//                                        <Td>
//                                           <Checkbox
//                                              isChecked={selectedStatements[statement.id] || false}
//                                              onChange={(e) => handleStatementSelection(statement.id, e.target.checked)}
//                                           />
//                                        </Td>
//                                        <Td>
//                                           <Badge colorScheme="blue" variant="subtle">
//                                              {statement.accountName}
//                                           </Badge>
//                                        </Td>
//                                        <Td>
//                                           <Text fontSize="sm" maxWidth="300px" isTruncated>
//                                              {statement.userNote || 'N/A'}
//                                           </Text>
//                                        </Td>
//                                        <Td>
//                                           <Text
//                                              fontSize="sm"
//                                              fontWeight="medium"
//                                              color={statement.txnType === 1 ? 'green.600' : 'red.600'}>
//                                              {formatAmount(statement.amount, statement.txnType)}
//                                           </Text>
//                                        </Td>
//                                        <Td>
//                                           <Text fontSize="sm">
//                                              {new Date(statement.txnDate).toLocaleDateString('en-IN')}
//                                           </Text>
//                                        </Td>
//                                     </Tr>
//                                  ))}
//                               </Tbody>
//                            </Table>
//                         </TableContainer>
//                      </Box>
//                   )}
//                </VStack>
//             </ModalBody>
//             <ModalFooter justifyContent="flex-end">
//                <Button
//                   mr={2}
//                   colorScheme="green"
//                   size="sm"
//                   onClick={handleFindAndReplace}
//                   isLoading={isImporting}
//                   loadingText="Updating..."
//                   isDisabled={getSelectedStatementIds().length === 0 || !accountName}>
//                   Find and Replace ({getSelectedStatementIds().length})
//                </Button>
//                <Button size="sm" onClick={onClose}>
//                   Close
//                </Button>
//             </ModalFooter>
//          </ModalContent>
//       </Modal>
//    );
// }

// import { useState, useRef, useEffect } from 'react';
// import {
//    Box,
//    Button,
//    Heading,
//    Modal,
//    ModalOverlay,
//    ModalContent,
//    ModalHeader,
//    ModalBody,
//    ModalFooter,
//    ModalCloseButton,
//    Select,
//    Input,
//    VStack,
//    HStack,
//    Alert,
//    AlertIcon,
//    useColorModeValue,
//    FormControl,
//    FormLabel,
//    Divider,
// } from '@chakra-ui/react';
// import axios from '../../../../axios';
// import { useDispatch, useSelector } from 'react-redux';
// import { useUser } from 'contexts';
// import { DAYBOOK_TYPE } from 'constants';
// import { getDaybookForPayments } from '../../../../redux/actions/daybook';
// import { getAccount } from '../../../../redux/actions/account';

// export default function BulkUpdateSuspenseAccount({
//    isBulkUpdateSuspenseAccount,
//    onClose,
//    handleBulkUpdateSuspenseAccount,
// }) {
//    const dispatch = useDispatch();
//    const [findName, setFindName] = useState('');
//    const [accountName, setAccountName] = useState('');
//    const accountsData = useSelector((state) => state.account.list || []);
//    const [bankStatements, setBankStatements] = useState([]);
//    const [selectedAllStatements, setSelectedAllStatements] = useState({});
//    const [isLoading, setIsLoading] = useState(false);
//    const [isImporting, setIsImporting] = useState(false);
//    const { user } = useUser();
//    const [message, setMessage] = useState('');
//    const [isOpen, setIsOpen] = useState(false);

//    const userCompanyId = Number(user?.loginhistory?.companyId);
//    const localCompanyId = Number(localStorage.getItem('companyId'));
//    const validCompanyId =
//       !isNaN(userCompanyId) && userCompanyId > 0
//          ? userCompanyId
//          : !isNaN(localCompanyId) && localCompanyId > 0
//          ? localCompanyId
//          : null;

//    // Refactored Effect 1: Fetch initial data (Daybooks and Suspense Account)
//    useEffect(() => {
//       if (validCompanyId) {
//          dispatch(getAccount({ compId: validCompanyId }));
//       } else {
//          console.warn('Skipping initial data fetch: Invalid companyId');
//       }
//    }, [dispatch, validCompanyId]);

//    const handleSave = () => {
//       // Close the Add/Edit form modal
//       setIsOpen(false);
//       // Re-fetch the suspense account to get the newly created one
//       if (validCompanyId) {
//          // dispatch(getVoucher({ name: 'SUSPENCE', compId: validCompanyId, page: 1, limit: 10 }));
//       }
//       // Give feedback to the user
//       setMessage('Suspense Account created. Please try importing again.');
//    };

//    const extractBankStatement = async () => {
//       if (!accountName) {
//          setMessage('Please select Account Name');
//          return;
//       }

//       setIsLoading(true);
//       setMessage('');

//       try {
//          const params = {
//             accountName: 'SUSPENCE',
//             userNote: findName,
//             isImported: 1,
//             companyId: validCompanyId,
//             page: 1,
//             limit: 10,
//             sortBy: 'id',
//             sortOrder: 'desc',
//          };

//          const response = await axios.get('/voucher/all', {
//             params: params,
//          });

//          const data = response.data.data;
//          console.log('data.', data);
//          setBankStatements(data || []);
//       } catch (error) {
//          setMessage(`Error: ${error.response.data.message}`);
//       } finally {
//          setIsLoading(false);
//       }
//    };
//    console.log('bankStatement', bankStatements);
//    const bgColor = useColorModeValue('gray.100', 'gray.700');
//    const headerBgColor = useColorModeValue('blue.50', 'blue.900');

//    return (
//       <Modal isOpen={isBulkUpdateSuspenseAccount} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
//          <ModalOverlay />
//          <ModalContent bg={bgColor}>
//             <ModalHeader display="flex" alignItems="center" bg={headerBgColor}>
//                <Heading size="sm">Bulk Update Suspense Account</Heading>
//             </ModalHeader>
//             <ModalCloseButton />
//             <ModalBody>
//                <VStack spacing={6} align="stretch">
//                   {/* Bank Selection and File Upload Section */}
//                   <Box>
//                      <HStack spacing={4} mb={4}>
//                         <FormControl flex="1">
//                            <FormLabel>Enter Account Name</FormLabel>
//                            <Input
//                               placeholder="Enter Account name"
//                               value={findName}
//                               onChange={(e) => setFindName(e.target.value)}
//                            />
//                         </FormControl>
//                         <FormControl flex="1">
//                            <FormLabel>Select Account</FormLabel>
//                            <Select
//                               placeholder="Choose a Account"
//                               value={accountName}
//                               onChange={(e) => setAccountName(e.target.value)}>
//                               {accountsData.map((bank, index) => (
//                                  <option key={`${bank.value}-${index}`} value={bank.value}>
//                                     {bank.name}
//                                  </option>
//                               ))}
//                            </Select>
//                         </FormControl>
//                         <Button
//                            colorScheme="blue"
//                            onClick={extractBankStatement}
//                            isLoading={isLoading}
//                            loadingText="Finding..."
//                            isDisabled={!accountName}
//                            alignSelf="end">
//                            Find
//                         </Button>
//                      </HStack>
//                      {message && (
//                         <Alert status={message.includes('Error') ? 'error' : 'success'} size="sm">
//                            <AlertIcon />
//                            {message}
//                         </Alert>
//                      )}
//                   </Box>

//                   <Divider />
//                </VStack>
//             </ModalBody>
//             <ModalFooter justifyContent="flex-end">
//                <Button
//                   mr={2}
//                   colorScheme="green"
//                   size="sm"
//                   // onClick={importSelectedAllStatements}
//                   // isLoading={isImporting}
//                   loadingText="Importing...">
//                   Find and Replace
//                </Button>
//                <Button size="sm" onClick={onClose}>
//                   Close
//                </Button>
//             </ModalFooter>
//          </ModalContent>
//       </Modal>
//    );
// }
